import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usersApi } from '../api/users';
import { User, Invitation, Role, ROLE_LABELS, ROLE_DESCRIPTIONS } from '../types/user';
import InviteUserModal from '../components/InviteUserModal';
import InviteLinkModal from '../components/InviteLinkModal';
import ConfirmDialog from '../components/ConfirmDialog';

const UserManagement: React.FC = () => {
  const { user: currentUser, logout } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [linkModal, setLinkModal] = useState<{ email: string; inviteUrl: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string } | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadData = async () => {
    try {
      const [usersData, invitationsData] = await Promise.all([
        usersApi.getCompanyUsers(),
        usersApi.getInvitations(),
      ]);
      setUsers(usersData);
      setInvitations(invitationsData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки данных');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRoleChange = async (userId: string, role: Role) => {
    try {
      await usersApi.changeRole(userId, role);
      setSuccess('Роль изменена');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteUser = async () => {
    if (!confirmDelete) return;
    try {
      await usersApi.removeUser(confirmDelete.id);
      setSuccess('Пользователь удален');
      setConfirmDelete(null);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleResendInvitation = async (id: string, email: string) => {
    try {
      const result = await usersApi.resendInvitation(id);
      loadData();
      if (result.delivered === 'email') {
        setSuccess(`Приглашение повторно отправлено на ${email}`);
        setTimeout(() => setSuccess(''), 3000);
      } else if (result.inviteUrl) {
        setLinkModal({ email, inviteUrl: result.inviteUrl });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteInvitation = async (id: string) => {
    try {
      await usersApi.deleteInvitation(id);
      setSuccess('Приглашение удалено');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleInviteSent = (result: { email: string; delivered: 'email' | 'link'; inviteUrl?: string }) => {
    setShowInviteModal(false);
    loadData();
    if (result.delivered === 'email') {
      setSuccess(`Приглашение отправлено на ${result.email}`);
      setTimeout(() => setSuccess(''), 3000);
    } else if (result.inviteUrl) {
      setLinkModal({ email: result.email, inviteUrl: result.inviteUrl });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-800">Пользователи</h1>
          <div className="flex items-center gap-3">
            <a href="/dashboard" className="text-blue-600 hover:underline text-sm whitespace-nowrap">
              Назад
            </a>
            <button onClick={logout} className="text-gray-500 hover:text-gray-700 text-sm whitespace-nowrap">
              Выйти
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-md text-sm">{error}</div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-100 text-green-800 rounded-md text-sm break-all">{success}</div>
        )}

        {/* Unified Users & Invitations */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800">Команда</h2>
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
            >
              Пригласить
            </button>
          </div>

          {(() => {
            const combined: Array<
              | { type: 'user'; data: User }
              | { type: 'invitation'; data: Invitation }
            > = [
              ...users.map((u) => ({ type: 'user' as const, data: u })),
              ...invitations
                .filter((inv) => inv.status !== 'accepted')
                .map((inv) => ({ type: 'invitation' as const, data: inv })),
            ];

            if (combined.length === 0) {
              return <p className="text-gray-500 text-sm">Нет пользователей</p>;
            }

            return (
              <>
                {/* Desktop table */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Имя</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Email</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Роль</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Статус</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {combined.map((row) => {
                        if (row.type === 'user') {
                          const u = row.data;
                          return (
                            <tr key={`user-${u.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-3">
                                <div className="flex items-center gap-2">
                                  {u.avatar && (
                                    <img src={u.avatar} alt="" className="w-6 h-6 rounded-full" />
                                  )}
                                  <span>{u.name}</span>
                                  {u.id === currentUser?.id && (
                                    <span className="text-xs text-gray-400">(вы)</span>
                                  )}
                                </div>
                              </td>
                              <td className="py-3 px-3 text-gray-600">{u.email}</td>
                              <td className="py-3 px-3">
                                {u.id === currentUser?.id ? (
                                  <span className="text-sm text-gray-700" title={ROLE_DESCRIPTIONS[u.role]}>{ROLE_LABELS[u.role]}</span>
                                ) : (
                                  <select
                                    value={u.role}
                                    onChange={(e) => handleRoleChange(u.id, e.target.value as Role)}
                                    className="text-sm border border-gray-300 rounded px-2 py-1"
                                    title={ROLE_DESCRIPTIONS[u.role]}
                                  >
                                    <option value="admin">{ROLE_LABELS.admin}</option>
                                    <option value="treasurer">{ROLE_LABELS.treasurer}</option>
                                    <option value="operator">{ROLE_LABELS.operator}</option>
                                  </select>
                                )}
                              </td>
                              <td className="py-3 px-3">
                                <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                  Активен
                                </span>
                              </td>
                              <td className="py-3 px-3 text-right">
                                {u.id !== currentUser?.id && (
                                  <button
                                    onClick={() => setConfirmDelete({ id: u.id, name: u.name })}
                                    className="text-red-600 hover:text-red-800 text-sm"
                                  >
                                    Удалить
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        } else {
                          const inv = row.data;
                          return (
                            <tr key={`inv-${inv.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-3 text-gray-400">—</td>
                              <td className="py-3 px-3 text-gray-600">{inv.email}</td>
                              <td className="py-3 px-3" title={ROLE_DESCRIPTIONS[inv.role]}>
                                <span className="text-sm text-gray-700">{ROLE_LABELS[inv.role]}</span>
                              </td>
                              <td className="py-3 px-3">
                                <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                                  Приглашён
                                </span>
                              </td>
                              <td className="py-3 px-3 text-right space-x-2">
                                <button
                                  onClick={() => handleResendInvitation(inv.id, inv.email)}
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                  Отправить заново
                                </button>
                                <button
                                  onClick={() => handleDeleteInvitation(inv.id)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                >
                                  Удалить
                                </button>
                              </td>
                            </tr>
                          );
                        }
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="sm:hidden space-y-3">
                  {combined.map((row) => {
                    if (row.type === 'user') {
                      const u = row.data;
                      return (
                        <div key={`user-${u.id}`} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            {u.avatar && (
                              <img src={u.avatar} alt="" className="w-7 h-7 rounded-full" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-800 truncate">
                                {u.name}
                                {u.id === currentUser?.id && (
                                  <span className="text-xs text-gray-400 ml-1">(вы)</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 truncate">{u.email}</div>
                            </div>
                            <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800 shrink-0">
                              Активен
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              {u.id === currentUser?.id ? (
                                <span className="text-sm text-gray-700" title={ROLE_DESCRIPTIONS[u.role]}>{ROLE_LABELS[u.role]}</span>
                              ) : (
                                <select
                                  value={u.role}
                                  onChange={(e) => handleRoleChange(u.id, e.target.value as Role)}
                                  className="text-sm border border-gray-300 rounded px-2 py-1"
                                  title={ROLE_DESCRIPTIONS[u.role]}
                                >
                                  <option value="admin">{ROLE_LABELS.admin}</option>
                                  <option value="treasurer">{ROLE_LABELS.treasurer}</option>
                                  <option value="operator">{ROLE_LABELS.operator}</option>
                                </select>
                              )}
                            </div>
                            {u.id !== currentUser?.id && (
                              <button
                                onClick={() => setConfirmDelete({ id: u.id, name: u.name })}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                Удалить
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    } else {
                      const inv = row.data;
                      return (
                        <div key={`inv-${inv.id}`} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-800 truncate">{inv.email}</div>
                              <div className="text-xs text-gray-500" title={ROLE_DESCRIPTIONS[inv.role]}>{ROLE_LABELS[inv.role]}</div>
                            </div>
                            <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800 shrink-0">
                              Приглашён
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => handleResendInvitation(inv.id, inv.email)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Отправить заново
                            </button>
                            <button
                              onClick={() => handleDeleteInvitation(inv.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Удалить
                            </button>
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              </>
            );
          })()}
        </div>
      </main>

      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onInviteSent={handleInviteSent}
        />
      )}

      {linkModal && (
        <InviteLinkModal
          email={linkModal.email}
          inviteUrl={linkModal.inviteUrl}
          onClose={() => setLinkModal(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          isOpen={true}
          title="Удалить пользователя"
          message={`Вы уверены, что хотите удалить ${confirmDelete.name}?`}
          confirmText="Удалить"
          cancelText="Отмена"
          variant="danger"
          onConfirm={handleDeleteUser}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default UserManagement;
